// ─── Animation Presets ────────────────────────────────────────
// Controlled motion language. Templates reference preset IDs.
// Never allow free-form animation configuration.

export const presets = {
  'none': {
    id: 'none',
    initial: {},
    animate: {},
    transition: {},
    viewport: { once: true },
  },

  'fade-up': {
    id: 'fade-up',
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    viewport: { amount: 0.25, once: true },
    mobile: { initial: { opacity: 0, y: 16 } },
  },

  'fade-in': {
    id: 'fade-in',
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.6, ease: 'easeOut' },
    viewport: { amount: 0.2, once: true },
  },

  'slide-left': {
    id: 'slide-left',
    initial: { opacity: 0, x: 32 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    viewport: { amount: 0.25, once: true },
    mobile: { initial: { opacity: 0, x: 20 } },
  },

  'slide-right': {
    id: 'slide-right',
    initial: { opacity: 0, x: -32 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    viewport: { amount: 0.25, once: true },
    mobile: { initial: { opacity: 0, x: -20 } },
  },

  'zoom-soft': {
    id: 'zoom-soft',
    initial: { opacity: 0, scale: 0.94 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    viewport: { amount: 0.2, once: true },
  },

  'stagger-text': {
    id: 'stagger-text',
    isStagger: true,
    containerDelay: 0.2,
    childStagger: 0.06,
    child: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
    },
  },

  'stagger-cards': {
    id: 'stagger-cards',
    isStagger: true,
    containerDelay: 0,
    childStagger: 0.1,
    child: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
    viewport: { amount: 0.2, once: true },
  },

  'parallax-soft': {
    id: 'parallax-soft',
    isParallax: true,
    range: { desktop: -40, mobile: -20 },
  },

  'reveal-mask': {
    id: 'reveal-mask',
    initial: { clipPath: 'inset(100% 0 0 0)', opacity: 0 },
    animate: { clipPath: 'inset(0% 0 0 0)', opacity: 1 },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    viewport: { amount: 0.2, once: true },
  },
};

/**
 * Get a resolved preset, applying mobile overrides and reduced-motion if needed.
 * @param {string} presetId
 * @param {boolean} isMobile
 * @param {boolean} reducedMotion - true when user prefers-reduced-motion
 * @returns {object}
 */
export function getPreset(presetId, isMobile = false, reducedMotion = false) {
  const preset = presets[presetId] || presets['fade-up'];

  // Reduced motion: collapse everything to a simple fade
  if (reducedMotion) {
    if (preset.isParallax) return presets['none'];
    if (preset.isStagger) {
      return {
        ...preset,
        containerDelay: 0,
        childStagger: 0,
        child: {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.3, ease: 'easeOut' },
        },
      };
    }
    return {
      ...preset,
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.3, ease: 'easeOut' },
    };
  }

  if (!isMobile || !preset.mobile) return preset;
  return {
    ...preset,
    initial: { ...preset.initial, ...preset.mobile.initial },
  };
}
